import { useState } from 'react';
import { AlertCircle, Printer } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Badge from '../../components/common/Badge';
import { formatCurrency } from '../../utils/helpers';

function DueTracking() {
  const { students, feeStructures, feePayments, classes, parents } = useApp();
  const [filterClass, setFilterClass] = useState('all');

  const dueStudents = students.filter(s=>s.status==='active').map(s=>{
    const fs = feeStructures.find(f=>f.classId===s.classId);
    if (!fs) return null;
    const prevBal = s.previousYearBalance || 0;
    const totalDue = fs.totalAmount + prevBal;
    const paid = feePayments.filter(p=>p.studentId===s.id).reduce((sum,p)=>sum+p.amount,0);
    const remaining = totalDue - paid;
    if (remaining <= 0) return null;
    const cls = classes.find(c=>c.id===s.classId);
    const parent = parents.find(p=>p.id===s.parentId);
    const status = paid > 0 ? 'partial' : 'pending';
    return { ...s, feeTotal:totalDue, paid, remaining, status, className:cls?`${cls.name}-${cls.section}`:'-', dueDate:fs.dueDate, parentPhone: parent?.phone || '—' };
  }).filter(Boolean);

  const filtered = filterClass === 'all' ? dueStudents : dueStudents.filter(s => s.classId === filterClass);
  const totalDue = filtered.reduce((sum,s)=>sum+s.remaining,0);
  const totalPaid = filtered.reduce((sum,s)=>sum+s.paid,0);
  const totalFee = filtered.reduce((sum,s)=>sum+s.feeTotal,0);

  const selectedClassName = filterClass === 'all' ? 'All Classes' : (() => { const c = classes.find(c=>c.id===filterClass); return c ? `${c.name}-${c.section}` : ''; })();

  const fmt = (n) => `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  const printReport = () => {
    const rows = filtered.map((s, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${s.firstName} ${s.lastName}</td>
        <td>${s.parentPhone}</td>
        <td>${s.className}</td>
        <td class="num">${fmt(s.feeTotal)}</td>
        <td class="num" style="color:#16a34a">${fmt(s.paid)}</td>
        <td class="num" style="color:#dc2626;font-weight:600">${fmt(s.remaining)}</td>
        <td><span class="badge badge-${s.status}">${s.status.charAt(0).toUpperCase() + s.status.slice(1)}</span></td>
      </tr>
    `).join('');

    const win = window.open('', '_blank', 'width=900,height=700');
    win.document.open(); win.document.write(`
      <html><head><title>Due Tracking Report</title>
      <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Roboto', Arial, sans-serif; padding: 28px 32px; color: #111; font-size: 13px; }
        .school-header { text-align:center; padding-bottom:10px; margin-bottom:16px; border-bottom:2px solid #1e3a8a; }
        .school-header h2 { color:#1e3a8a; font-size:20px; margin-bottom:2px; }
        .school-header p { font-size:12px; color:#555; }
        .meta { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:16px; padding-bottom:12px; border-bottom:1px solid #e5e7eb; }
        .meta-left .title { font-size:16px; font-weight:700; color:#1e3a8a; }
        .meta-left .sub { font-size:12px; color:#555; margin-top:3px; }
        .meta-right { text-align:right; font-size:12px; color:#555; }
        .summary { display:flex; gap:16px; margin-bottom:16px; }
        .summary-box { flex:1; padding:10px 14px; border-radius:8px; border:1px solid #e5e7eb; }
        .summary-box .lbl { font-size:11px; color:#888; margin-bottom:2px; }
        .summary-box .val { font-size:15px; font-weight:700; }
        table { width:100%; border-collapse:collapse; }
        thead th { background:#f1f5f9; color:#374151; font-weight:600; font-size:12px; padding:8px 10px; text-align:left; border-bottom:2px solid #d1d5db; }
        thead th.num { text-align:right; }
        td { padding:7px 10px; border-bottom:1px solid #f3f4f6; vertical-align:middle; font-size:12px; }
        tr.total-row td { background:#f1f5f9; font-weight:700; border-top:2px solid #d1d5db; border-bottom:none; }
        .num { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
        .badge { display:inline-block; padding:1px 9px; border-radius:20px; font-size:11px; font-weight:600; }
        .badge-partial { background:#fef3c7; color:#92400e; }
        .badge-pending { background:#fee2e2; color:#991b1b; }
        .footer { text-align:center; font-size:11px; color:#aaa; margin-top:24px; padding-top:8px; border-top:1px dashed #e5e7eb; }
        .print-btn { text-align:center; margin-top:18px; }
        @media print { .print-btn { display:none; } body { padding:16px; } }
      </style></head><body>
      <div class="school-header">
        <h2>Vasavya Vidyalayam</h2>
        <p>Fee Due Tracking Report &mdash; Academic Year 2025-2026</p>
      </div>
      <div class="meta">
        <div class="meta-left">
          <div class="title">Due Tracking — ${selectedClassName}</div>
          <div class="sub">${filtered.length} student${filtered.length !== 1 ? 's' : ''} with pending fees</div>
        </div>
        <div class="meta-right">
          <div>Date: <strong>${new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</strong></div>
        </div>
      </div>
      <div class="summary">
        <div class="summary-box">
          <div class="lbl">Total Fee</div>
          <div class="val">${fmt(totalFee)}</div>
        </div>
        <div class="summary-box">
          <div class="lbl">Total Paid</div>
          <div class="val" style="color:#16a34a">${fmt(totalPaid)}</div>
        </div>
        <div class="summary-box">
          <div class="lbl">Total Due</div>
          <div class="val" style="color:#dc2626">${fmt(totalDue)}</div>
        </div>
        <div class="summary-box">
          <div class="lbl">Collection Rate</div>
          <div class="val">${totalFee > 0 ? Math.round((totalPaid/totalFee)*100) : 0}%</div>
        </div>
      </div>
      <table>
        <thead><tr>
          <th>#</th>
          <th>Student</th>
          <th>Parent Phone</th>
          <th>Class</th>
          <th class="num">Total Fee (₹)</th>
          <th class="num">Paid (₹)</th>
          <th class="num">Remaining (₹)</th>
          <th>Status</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr class="total-row">
            <td colspan="4">Grand Total</td>
            <td class="num">${fmt(totalFee)}</td>
            <td class="num" style="color:#16a34a">${fmt(totalPaid)}</td>
            <td class="num" style="color:#dc2626">${fmt(totalDue)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      <div class="footer">Computer-generated report &mdash; Vasavya Vidyalayam</div>
      <div class="print-btn">
        <button onclick="window.print()" style="background:#1e3a8a;color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:14px;cursor:pointer;font-family:'Roboto',Arial,sans-serif">🖨 Print</button>
      </div>
      </body></html>
    `); win.document.close();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Due Tracking</h1><p className="mt-0.5">{filtered.length} students with pending fees</p></div>
        <div className="flex items-center gap-3">
          <select value={filterClass} onChange={e=>setFilterClass(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            <option value="all">All Classes</option>
            {classes.map(c=><option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
          </select>
          <button onClick={printReport} className="flex items-center gap-1.5 px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            <Printer size={14}/> Print
          </button>
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-1 flex items-center gap-2">
            <AlertCircle size={16} className="text-red-500"/>
            <span className="font-semibold text-red-700">Total Due: {formatCurrency(totalDue)}</span>
          </div>
        </div>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <p className="mb-1">Total Fee</p>
          <p className="text-lg font-bold">{formatCurrency(totalFee)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <p className="mb-1" style={{ color: '#16a34a' }}>Total Paid</p>
          <p className="text-lg font-bold" style={{ color: '#16a34a' }}>{formatCurrency(totalPaid)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <p className="mb-1" style={{ color: '#dc2626' }}>Total Due</p>
          <p className="text-lg font-bold" style={{ color: '#dc2626' }}>{formatCurrency(totalDue)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <p className="mb-1">Collection Rate</p>
          <p className="text-lg font-bold">{totalFee > 0 ? Math.round((totalPaid / totalFee) * 100) : 0}%</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-4 py-1 text-left font-semibold">Student</th>
              <th className="px-4 py-1 text-left font-semibold">Class</th>
              <th className="px-4 py-1 text-right font-semibold">Total Fee</th>
              <th className="px-4 py-1 text-right font-semibold">Paid</th>
              <th className="px-4 py-1 text-right font-semibold">Remaining</th>
              <th className="px-4 py-1 text-center font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map(s=>(
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="px-4 py-1 font-medium">{s.firstName} {s.lastName}</td>
                <td className="px-4 py-1">{s.className}</td>
                <td className="px-4 py-1 text-right">{formatCurrency(s.feeTotal)}</td>
                <td className="px-4 py-1 text-right text-green-600">{formatCurrency(s.paid)}</td>
                <td className="px-4 py-1 text-right font-semibold text-red-600">{formatCurrency(s.remaining)}</td>
                <td className="px-4 py-1 text-center"><Badge value={s.status}/></td>
              </tr>
            ))}
            {filtered.length===0 && <tr><td colSpan="6" className="py-8 text-center">All fees are cleared!</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
export default DueTracking;
